import useSuccessToast, { showErrorToast } from "@configs/react-toastify/snackbar";
import { formatDate, formatDateTime, toKebabCase, translateDepartment } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useNavigate } from "react-router-dom";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal } from "@components/UI";
import queryKeys from "@configs/react-query/query-key";
import { CLARITY_STATUS_OPTIONS, FOSSID_STATUS_OPTIONS, SBOM_STATUS_OPTIONS, TOAST } from "@constants";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { SCAN_TOOL_OPTIONS } from "@constants/scanTool";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";
import { LOCAL_STORAGE } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import { extractErrorCode } from "@utils/error";

const SEARCH_FIELDS = {
  PROJECT_ID: "projectId",
  PRODUCT_NAME: "productName",
  PROJECT_MANAGER_ID: "projectManagerId",
  ASSET_CLASSIFICATION_NAME: "assetClassificationName",
  // CREATER_DEPT_CD: "createrDeptCd",
  CREATER_ID: "createrId",
  COMPLETION_REQUEST_DT: "completionRequestDt",
  CREATED_AT_FROM: "createdAtFrom",
  CREATED_AT_TO: "createdAtTo",
  BINARY_VERIFICATION_STATUS_CD: "binaryVerificationStatusCd",
  SOURCE_CODE_VERIFICATION_STATUS_CD: "sourceCodeVerificationStatusCd",
  SBOM_VERIFICATION_STATUS_CD: "sbomVerificationStatusCd",
  CLARITY_AIR_STATUS_CD: "clarityAirVerificationStatusCd",
};

const TABLE_COLUMNS = {
  PROJECT_ID: "projectId",
  PROJECT_NAME: "projectName",
  PROJECT_MANAGER_NAME: "projectManagerName",
  PRODUCT_NAME: "productName",
  ASSET_CLASSIFICATION_NAME: "assetClassificationName",
  VERIFICATION_STATUS: "verificationStatus",
  COMPLETION_REQUEST_DT: "completionRequestDt",
  // CREATER_DEPT_CD_NAME: "createrDeptCdName",
  CREATER_NAME: "createrName",
  CREATED_AT: "createdAt",
};

const STATUS_NAME_MAPPING = {
  [SEARCH_FIELDS.BINARY_VERIFICATION_STATUS_CD]: {
    USAGE_TYPE_REVIEW: "Request Usage Type Review",
    USAGE_TYPE_CHECK: "Usage Type Review",
    USAGE_TYPE_INPUT: "Usage Type Input",
  },
  [SEARCH_FIELDS.SOURCE_CODE_VERIFICATION_STATUS_CD]: {
    USAGE_TYPE_CHECK: "Usage Type Check",
    USAGE_TYPE_INPUT: "Usage Type Input",
  },
};


const INITIAL_SEARCH_CRITERIA = {
  [SEARCH_FIELDS.PROJECT_ID]: "",
  [SEARCH_FIELDS.PRODUCT_NAME]: "",
  [SEARCH_FIELDS.PROJECT_MANAGER_ID]: "",
  [SEARCH_FIELDS.CREATER_ID]: "",
  [SEARCH_FIELDS.COMPLETION_REQUEST_DT]: "",
  [SEARCH_FIELDS.CREATED_AT_FROM]: "",
  [SEARCH_FIELDS.CREATED_AT_TO]: "",
  [SEARCH_FIELDS.BINARY_VERIFICATION_STATUS_CD]: "",
  [SEARCH_FIELDS.SOURCE_CODE_VERIFICATION_STATUS_CD]: "",
  [SEARCH_FIELDS.SBOM_VERIFICATION_STATUS_CD]: "",
  [SEARCH_FIELDS.CLARITY_AIR_STATUS_CD]: "",
  [SEARCH_FIELDS.ASSET_CLASSIFICATION_NAME]: "",
};

const ProjectList = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { hasAddPermission, hasDeletePermission } = useRoutePermissions();
  const { hasManagePermission } = useContext(PermissionContext);
  const currentUserId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchCriteria, setSearchCriteria] = useState(INITIAL_SEARCH_CRITERIA);
  const [debouncedSearchCriteria, setDebouncedSearchCriteria] = useState(searchCriteria);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const [projectList, setProjectList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.PROJECT_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isProjectListLoading, setIsProjectListLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(TABLE_COLUMNS.CREATED_AT);
  const [sortDirection, setSortDirection] = useState("desc");

  const {
    creatorList,
    departmentList,
    productsList,
    projectManagerList,
    scanToolList,
    assetClassificationsList,
    fetchSearchInfo,
  } = useSearchInfo();

  useEffect(() => {
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchCriteria, sortColumn, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, debouncedSearchCriteria, sortColumn, sortDirection]);

  // Listen for fossid_import_complete event to refresh project list
  useEffect(() => {
    const handleFossidImportComplete = () => {
      fetchData();
    };

    window.addEventListener('fossid_import_complete', handleFossidImportComplete);

    return () => {
      window.removeEventListener('fossid_import_complete', handleFossidImportComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchData uses current state values, so we don't need to include dependencies

  const allowScanToolKeys = useMemo(
    () => (scanToolList ?? []).map((t) => t.value.toLowerCase()),
    [scanToolList]
  );

  // Debounce search input to avoid calling API too frequently
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Show loading when user is typing (only when there's actual change)
    const hasActualChange = Object.values(searchCriteria).some((value) => value && value !== "");
    if (hasActualChange) {
      setIsSearching(true);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchCriteria(searchCriteria);
      setIsSearching(false);
    }, 0);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchCriteria]);

  const columns = [
    TABLE_COLUMNS.PROJECT_ID,
    TABLE_COLUMNS.PROJECT_NAME,
    TABLE_COLUMNS.PROJECT_MANAGER_NAME,
    TABLE_COLUMNS.PRODUCT_NAME,
    TABLE_COLUMNS.ASSET_CLASSIFICATION_NAME,
    TABLE_COLUMNS.VERIFICATION_STATUS,
    TABLE_COLUMNS.COMPLETION_REQUEST_DT,
    // TABLE_COLUMNS.CREATER_DEPT_CD_NAME,
    TABLE_COLUMNS.CREATER_NAME,
    TABLE_COLUMNS.CREATED_AT,
  ];

  const getLatestVerificationStatus = (row) => {
    const {
      binaryCreatedAt,
      sourceCodeCreatedAt,
      sbomCreatedAt,
      clarityAirCreatedAt,
      clarityAirVerificationStatusCd,
      clarityAirVerificationStatusCdName,
      binaryVerificationStatusCd,
      sourceCodeVerificationStatusCd,
      sbomVerificationStatusCd,
      binaryVerificationStatusCdName,
      sourceCodeVerificationStatusCdName,
      sbomVerificationStatusCdName,
    } = row;

    // Prepare mapping of each scan tool with its created time, status code, and status name
    const toolData = [
      {
        key: SCAN_TOOL_OPTIONS.clarityAIR.key,
        name: SCAN_TOOL_OPTIONS.clarityAIR.name,
        time: clarityAirCreatedAt ? new Date(clarityAirCreatedAt).getTime() : 0,
        status: clarityAirVerificationStatusCd,
        statusName: clarityAirVerificationStatusCdName,
      },
      {
        key: SCAN_TOOL_OPTIONS.clarity.key,
        name: SCAN_TOOL_OPTIONS.clarity.name,
        time: binaryCreatedAt ? new Date(binaryCreatedAt).getTime() : 0,
        status: binaryVerificationStatusCd,
        statusName: binaryVerificationStatusCdName,
      },
      {
        key: SCAN_TOOL_OPTIONS.fossid.key,
        name: SCAN_TOOL_OPTIONS.fossid.name,
        time: sourceCodeCreatedAt ? new Date(sourceCodeCreatedAt).getTime() : 0,
        status: sourceCodeVerificationStatusCd,
        statusName: sourceCodeVerificationStatusCdName,
      },
      {
        key: SCAN_TOOL_OPTIONS.sbom.key,
        name: SCAN_TOOL_OPTIONS.sbom.name,
        time: sbomCreatedAt ? new Date(sbomCreatedAt).getTime() : 0,
        status: sbomVerificationStatusCd,
        statusName: sbomVerificationStatusCdName,
      }
    ];

    // Keep only the tools that exist in scanTools list (skip others).
    // Keys must match case-insensitively: allowScanToolKeys are lowercased but clarityAIR.key is camelCase.
    const filteredTools = toolData.filter(
      (t) =>
        allowScanToolKeys.includes(String(t.key).toLowerCase()) && t.status,
    );

    // Find the tool with the latest created time
    const latestTool = filteredTools.reduce((latest, curr) => {
      return curr.time > (latest?.time || 0) ? curr : latest;
    }, null);

    // Return the status information of the latest tool
    if (latestTool && latestTool.status) {
      return {
        status: latestTool.status,
        toolName: latestTool.name,
        statusName: latestTool.statusName,
      };
    }

    // Fallback: return the first available status
    for (const t of filteredTools) {
      if (t.status) {
        return {
          status: t.status,
          toolName: t.name,
          statusName: t.statusName,
        };
      }
    }

    return { status: null, toolName: null, statusName: null };
  };

  // Shared helper function to get translated status text
  const getStatusDisplayText = useCallback((status, statusName) => {
    if (!status) return "";

    const statusText = {
      ON_ANALYSIS: t("status.analyzing"),
      ANALYSIS_CHECK: t("status.analysisCheck"),
      REQUEST: t("status.request"),
      ON_VERIFICATION: t("status.onVerification"),
      ON_REVIEW: t("status.onReview"),
      STANDBY_FOLLOWUP: t("status.standbyFollowup"),
      FOLLOWUP_CHECK: t("status.followupCheck"),
      DONE: t("status.done"),
      REVERIFICATION: t("status.reverification"),
      ERROR: t("status.error"),
    };

    let displayText = statusText[status] || null;

    // For ON_VERIFICATION status, use the specific status name if available
    if (status === "ON_VERIFICATION" && statusName) {
      const statusNameMap = {
        "Request Usage Type Review": t("status.usageTypeReview"),
        "Usage Type Check": t("status.usageTypeCheck"),
        "Usage Type Input": t("status.usageTypeInput"),
        "Usage Type Review": t("status.usage-type-review")
      };
      displayText = statusNameMap[statusName] || statusName;
    }

    return displayText;
  }, [t]);

  const renderVerificationStatusCell = (value, row) => {
    const { status, toolName, statusName } = getLatestVerificationStatus(row);

    if (!status) {
      return null;
    }

    const statusStyles = {
      ON_ANALYSIS: colors.textStatusAnalysis,
      ANALYSIS_CHECK: colors.textStatusAnalysis,
      REQUEST: colors.textStatusRequest,
      ON_VERIFICATION: colors.textStatusVerification,
      ON_REVIEW: colors.textStatusReview,
      STANDBY_FOLLOWUP: colors.textStatusFollowup,
      FOLLOWUP_CHECK: colors.textStatusFollowup,
      DONE: colors.textStatusSuccess,
      REVERIFICATION: colors.textStatusReverification,
      ERROR: colors.textStatusAnalysis,
    };

    const statusColorDot = {
      ON_ANALYSIS: colors.bgStatusAnalysis,
      ANALYSIS_CHECK: colors.bgStatusAnalysis,
      REQUEST: colors.bgStatusRequest,
      ON_VERIFICATION: colors.bgStatusVerification,
      ON_REVIEW: colors.bgStatusReview,
      STANDBY_FOLLOWUP: colors.bgStatusFollowup,
      FOLLOWUP_CHECK: colors.bgStatusFollowup,
      DONE: colors.bgStatusSuccess,
      REVERIFICATION: colors.bgStatusReverification,
      ERROR: colors.bgStatusAnalysis,
    };

    const displayText = getStatusDisplayText(status, statusName);

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 text-sm text-[13px] leading-none font-semibold ${statusStyles[status]}`}
      >
        <span className={`${colors.textNeutralLight} mr-2`}>{toolName}</span>
        <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status]}`}></div>
        {displayText}
      </span>
    );
  };

  const columnConfig = {
    projectId: {
      headerRender: () => t("project.projectId"),
      style: {
        width: "12%",
        minWidth: "120px",
      },
      truncate: true,
    },
    projectName: {
      headerRender: () => t("project.projectName"),
      style: {
        width: "12%",
        minWidth: "100px",
      },
      truncate: true,
    },
    projectManagerName: {
      headerRender: () => t("project.project-manager"),
      style: {
        width: "10%",
        minWidth: "90px",
        whiteSpace: "normal",
        overflowWrap: "initial",
        verticalAlign: "middle",
      },
      customRender: (value, row) => (
        <UserNameWithId name={value || row.projectManagerName} id={row.projectManagerId} />
      ),
    },
    productName: {
      headerRender: () => t("project.productName"),
      style: {
        width: "12%",
        minWidth: "100px",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      customRender: (value, row) => {
        const productName = row.product?.productName || "";
        return (
          <span
            title={productName}
            className={`text-sm ${colors.textPrimary}`}
          >
            {productName || "---"}
          </span>
        );
      },
    },
    assetClassificationName: {
      headerRender: () => t("project.assetClassificationName"),
      style: {
        width: "12%",
        minWidth: "100px",
      },
      truncate: true,
    },
    verificationStatus: {
      style: {
        width: "16%",
        minWidth: "140px",
      },
      headerRender: () => t("project.verificationStatus"),
      customRender: (value, row) => renderVerificationStatusCell(value, row),
    },
    completionRequestDt: {
      style: {
        width: "10%",
        minWidth: "90px",
      },
      headerRender: () => t("project.completion-request-date"),
      customRender: (value, row) => formatDate(row.completionRequestDt),
    },
    createrName: {
      headerRender: () => t("project.createrName"),
      style: {
        width: "8%",
        minWidth: "80px",
      },
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      headerRender: () => t("project.createdAt"),
      style: {
        width: "8%",
        minWidth: "120px",
      },
      customRender: (value, row) => formatDateTime(row.createdAt),
    },
  };

  const searchFields = [
    { name: SEARCH_FIELDS.PROJECT_ID, label: t("project.projectId"), placeholder: t("project.projectId") },
    {
      name: SEARCH_FIELDS.PRODUCT_NAME,
      label: t("project.productName"),
      type: "select",
      placeholder: t("project.productName"),
      options: productsList,
    },
    {
      name: SEARCH_FIELDS.PROJECT_MANAGER_ID,
      label: t("project.project-manager"),
      type: "select",
      placeholder: t("project.project-manager"),
      options: projectManagerList,
      displayFormatLabelWithID: true,
    },
    // {
    //   name: SEARCH_FIELDS.CREATER_DEPT_CD,
    //   label: t("project.createrDeptCd"),
    //   type: "select",
    //   placeholder: t("project.createrDeptCd"),
    //   options: departmentList.map((item) => ({
    //     label: translateDepartment(t, item.value),
    //     value: item.value,
    //   })),
    // },
    {
      name: SEARCH_FIELDS.CREATER_ID,
      label: t("project.creator"),
      type: "select",
      placeholder: t("project.creator"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: SEARCH_FIELDS.COMPLETION_REQUEST_DT,
      label: t("project.completion-request-date"),
      placeholder: t("project.completion-request-date"),
      type: "date",
    },
    {
      name: SEARCH_FIELDS.CREATED_AT_FROM,
      type: "date",
      placeholder: t("common.create-date-from"),
    },
    {
      name: SEARCH_FIELDS.CREATED_AT_TO,
      type: "date",
      placeholder: t("common.create-date-to"),
    },
    {
      name: SEARCH_FIELDS.ASSET_CLASSIFICATION_NAME,
      label: t("project.assetClassificationName"),
      type: "select",
      placeholder: t("project.assetClassificationName"),
      options: assetClassificationsList,
    },
  ];

  const verificationStatusSearchField = [
    {
      key: SCAN_TOOL_OPTIONS.clarity.key,
      name: SEARCH_FIELDS.BINARY_VERIFICATION_STATUS_CD,
      label: t("project.binaryVerificationStatusCd"),
      type: "select",
      placeholder: t("project.binaryVerificationStatusCd"),
      options: CLARITY_STATUS_OPTIONS.map((item) => ({
        label: toKebabCase(item.label) !== "" ? t(`common.${toKebabCase(item.label)}`) : item.label,
        value: item.value,
      })),
    },
    {
      key: SCAN_TOOL_OPTIONS.clarityAIR.key,
      name: SEARCH_FIELDS.CLARITY_AIR_STATUS_CD,
      label: t("project.clarityAirStatusFilter"),
      type: "select",
      placeholder: t("project.clarityAirStatusFilter"),
      options: FOSSID_STATUS_OPTIONS.map((item) => ({
        label: toKebabCase(item.label) !== "" ? t(`common.${toKebabCase(item.label)}`) : item.label,
        value: item.value,
      })),
    },
    {
      key: SCAN_TOOL_OPTIONS.fossid.key,
      name: SEARCH_FIELDS.SOURCE_CODE_VERIFICATION_STATUS_CD,
      label: t("project.sourceCodeVerificationStatusCd"),
      type: "select",
      placeholder: t("project.sourceCodeVerificationStatusCd"),
      options: FOSSID_STATUS_OPTIONS.map((item) => ({
        label: toKebabCase(item.label) !== "" ? t(`common.${toKebabCase(item.label)}`) : item.label,
        value: item.value,
      })),
    },
    {
      key: SCAN_TOOL_OPTIONS.sbom.key,
      name: SEARCH_FIELDS.SBOM_VERIFICATION_STATUS_CD,
      label: t("project.sbomVerificationStatusCd"),
      placeholder: t("project.sbomVerificationStatusCd"),
      type: "select",
      options: SBOM_STATUS_OPTIONS.map((item) => ({
        label: toKebabCase(item.label) !== "" ? t(`common.${toKebabCase(item.label)}`) : item.label,
        value: item.value,
      })),
    },
  ];

  const filteredVerificationStatusSearchField = useMemo(() => {
    return verificationStatusSearchField.filter((item) =>
      allowScanToolKeys.includes(String(item.key).toLowerCase()),
    );
  }, [allowScanToolKeys, verificationStatusSearchField]);

  const allAdvancedSearchFields = useMemo(
    () => [...searchFields, ...filteredVerificationStatusSearchField],
    [searchFields, filteredVerificationStatusSearchField],
  );

  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: allAdvancedSearchFields,
    quickSearchField: "search",
    setSearchCriteria,
    initialCriteria: INITIAL_SEARCH_CRITERIA,
    onOpen: fetchSearchInfo,
  });

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const handleSort = (columnKey, direction) => {
    setSortColumn(columnKey);
    setSortDirection(direction);
    // Page will reset automatically via useEffect dependency
  };

  const handleDeleteProject = () => {
    setShowDeleteModal(true);
  };

  const deleteData = useMutationData(
    queryKeys.removeProject,
    {
      method: "DELETE",
    },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.PROJECTS, selectedRows);
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        setSelectedRows([]);
        useSuccessToast(TOAST.success, t("common.successfully-deleted"), t("common.project-has-been-deleted"));
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        const errorCode = extractErrorCode(error, "errorcode.00001");
        showErrorToast(t("common.error"), t(errorCode));
      },
    },
  );

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      // Implement actual delete API call here
      const bodyRequest = {
        ids: selectedRows,
      };
      await deleteData.mutateAsync(bodyRequest);

      setSelectedRows([]);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting projects:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Cleanup effect to clear debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleAddProject = () => {
    navigate("/projects/new");
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const canDeleteProject = (row) => {
    // Exception: Manage permission can delete all projects
    if (hasManagePermission) return true;
    if (!row?.createrId) return false;
    if (!currentUserId) return false;
    return row.createrId === currentUserId;
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.projectSeq}`);
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(debouncedSearchCriteria, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined && value !== null) {
        if (key === SEARCH_FIELDS.PRODUCT_NAME) {
          const selectedProduct = productsList.find(product => product.value === value);
          acc.productSeq = selectedProduct ? parseInt(selectedProduct.value, 10) : parseInt(value, 10);
        } else if (key === SEARCH_FIELDS.ASSET_CLASSIFICATION_NAME) {
          const selectedAsset = assetClassificationsList?.find((item) => item.value === value);
          acc[key] = selectedAsset ? selectedAsset.label : value;
        } else if (
          (key === SEARCH_FIELDS.COMPLETION_REQUEST_DT || key === SEARCH_FIELDS.CREATED_AT_FROM || key === SEARCH_FIELDS.CREATED_AT_TO) &&
          value instanceof Date
        ) {
          acc[key] = formatDate(value); // Format: YYYY-MM-DD
        } else {
          acc[key] = value;
        }

        // Convert special *_VerificationStatusCd filters to *_VerificationStatusCdName for BE matching
        const statusMap = STATUS_NAME_MAPPING[key];
        if (statusMap && statusMap[value]) {
          acc[
            key === SEARCH_FIELDS.BINARY_VERIFICATION_STATUS_CD
              ? "binaryVerificationStatusCdName"
              : "sourceCodeVerificationStatusCdName"
          ] = statusMap[value];
          delete acc[key];
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  // Helper function to get translated status text for sorting (reuses getStatusDisplayText)
  const getStatusTextForSort = useCallback((status, statusName) => {
    return getStatusDisplayText(status, statusName);
  }, [getStatusDisplayText]);

  const getStatusNameByType = (item) => {
    switch (item.verificationType) {
      case "Clarity":
        return item.binaryVerificationStatusCdName;
      case "FossID":
        return item.sourceCodeVerificationStatusCdName;
      case "SBOM":
        return item.sbomVerificationStatusCdName;
      case "ClarityAIR":
        return item.clarityAirStatusCdName;
      default:
        return null;
    }
  };

  const getProjectProductSeq = (project) => {
    if (project?.product?.productSeq != null) return parseInt(project.product.productSeq, 10);
    if (project?.productSeq != null) return parseInt(project.productSeq, 10);
    return null;
  };

  const getProjectProductName = (project) => {
    return project?.product?.productName || "";
  };

  // Helper function to filter projects by product and basic search (IndexedDB client-side)
  const filterProjectsBySearch = useCallback((projects, productSeqFilter, productName, basicSearch) => {
    let filtered = projects;

    if (productSeqFilter != null && productSeqFilter !== "") {
      const targetSeq = parseInt(productSeqFilter, 10);
      filtered = filtered.filter((project) => getProjectProductSeq(project) === targetSeq);
    } else if (productName && productName.trim()) {
      const nameLower = productName.toLowerCase();
      filtered = filtered.filter((project) =>
        getProjectProductName(project).toLowerCase().includes(nameLower)
      );
    }

    if (basicSearch && basicSearch.trim()) {
      const searchTerm = basicSearch.toLowerCase();
      filtered = filtered.filter((project) => {
        const matchesProjectInfo =
          (project.projectId && project.projectId.toLowerCase().includes(searchTerm)) ||
          (project.projectName && project.projectName.toLowerCase().includes(searchTerm));

        const matchesProduct = getProjectProductName(project).toLowerCase().includes(searchTerm);

        const matchesOtherFields =
          (project.assetClassificationName && project.assetClassificationName.toLowerCase().includes(searchTerm)) ||
          (project.projectManagerId && project.projectManagerId.toLowerCase().includes(searchTerm)) ||
          (project.projectManagerName && project.projectManagerName.toLowerCase().includes(searchTerm)) ||
          (project.createrId && project.createrId.toLowerCase().includes(searchTerm)) ||
          (project.createrName && project.createrName.toLowerCase().includes(searchTerm));

        return matchesProjectInfo || matchesProduct || matchesOtherFields;
      });
    }

    return filtered;
  }, []);

  const sortByVerificationStatus = useCallback((projects, order) => {
    const locale = i18n.language || "ko";
    const normalizedLocale = locale.toLowerCase().split("-")[0];

    //Map first to calculate status text once per project
    const projectsWithText = projects.map((p) => ({
      ...p,
      _sortStatusText: getStatusTextForSort(
        p.verificationStatusCd,
        getStatusNameByType(p)
      ),
    }));

    return projectsWithText.sort((a, b) => {
      const aText = a._sortStatusText;
      const bText = b._sortStatusText;

      // Null / undefined handling
      const aNull = !aText;
      const bNull = !bText;

      if (aNull && bNull) return 0;
      if (aNull) return order === "asc" ? -1 : 1;
      if (bNull) return order === "asc" ? 1 : -1;

      // Use locale-aware comparison
      const cmp = aText.localeCompare(bText, normalizedLocale, {
        sensitivity: "base",
        numeric: true
      });

      return order === "asc" ? cmp : -cmp;
    });
  }, [getStatusTextForSort, allowScanToolKeys]);

  const sortByProductName = useCallback((projects, order) => {
    const locale = i18n.language || "ko";
    const normalizedLocale = locale.toLowerCase().split("-")[0];

    return [...projects].sort((a, b) => {
      const aText = getProjectProductName(a);
      const bText = getProjectProductName(b);

      const aNull = !aText;
      const bNull = !bText;

      if (aNull && bNull) return 0;
      if (aNull) return order === "asc" ? -1 : 1;
      if (bNull) return order === "asc" ? 1 : -1;

      const cmp = aText.localeCompare(bText, normalizedLocale, {
        sensitivity: "base",
        numeric: true,
      });

      return order === "asc" ? cmp : -cmp;
    });
  }, []);


  const getProjectSeqs = useMutationData(
    ({ clarityStatus, fossidStatus, sbomStatus, clarityAirStatus }) => {
      const params = new URLSearchParams();
      if (clarityStatus) params.append('clarityStatus', clarityStatus);
      if (fossidStatus) params.append('fossidStatus', fossidStatus);
      if (sbomStatus) params.append('sbomStatus', sbomStatus);
      if (clarityAirStatus) params.append('clarityAirStatus', clarityAirStatus);
      return `projects/project-seqs-multiple?${params.toString()}`;
    },
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
      },
      onError: (error) => {
        console.error("Error fetching project seqs:", error);
      },
    },
  );

  const getListData = async (bodyRequest = {}) => {
    setIsProjectListLoading(true);
    try {
      let projectSeqs = [];
      const clarityStatus = bodyRequest?.binaryVerificationStatusCdName || bodyRequest?.binaryVerificationStatusCd || null;
      const fossidStatus = bodyRequest?.sourceCodeVerificationStatusCdName || bodyRequest?.sourceCodeVerificationStatusCd || null;
      const sbomStatus = bodyRequest?.sbomVerificationStatusCd || null;
      const clarityAirStatus = bodyRequest?.clarityAirVerificationStatusCd || null;
      const hasAnyVerificationStatus =
        (clarityStatus && clarityStatus !== "") ||
        (fossidStatus && fossidStatus !== "") ||
        (sbomStatus && sbomStatus !== "") ||
        (clarityAirStatus && clarityAirStatus !== "");

      if (hasAnyVerificationStatus) {
        projectSeqs = await getProjectSeqs.mutateAsync({
          clarityStatus: clarityStatus,
          fossidStatus: fossidStatus,
          sbomStatus: sbomStatus,
          clarityAirStatus: clarityAirStatus,
        });
      }
      const { pageSize = itemsPerPage, startRow = 0, ...filters } = bodyRequest;

      // Extract productSeq search criteria for client-side filtering
      const productSeqFilter = filters.productSeq;
      const selectedProductName = productSeqFilter
        ? (productsList.find((product) => String(product.value) === String(productSeqFilter))?.label || "")
        : "";

      // Extract basic search criteria
      const basicSearchTerm = filters.search || '';

      // Remove verification status fields, productSeq, and search from filters since we handle them separately
      const {
        binaryVerificationStatusCdName,
        binaryVerificationStatusCd,
        sourceCodeVerificationStatusCdName,
        sourceCodeVerificationStatusCd,
        sbomVerificationStatusCd,
        productSeq,
        search,
        clarityAirVerificationStatusCd,
        clarityAirVerificationStatusCdName,
        ...otherFilters
      } = filters;

      // Map sort column to database field name
      const dbSortKey = sortColumn || "createdAt";
      const dbSortOrder = sortDirection || "desc";

      // Special handling for nested/computed columns that IndexedDB cannot sort by flat key
      if (
        dbSortKey === TABLE_COLUMNS.VERIFICATION_STATUS ||
        dbSortKey === TABLE_COLUMNS.PRODUCT_NAME
      ) {
        // Fetch all filtered data without pagination; sort in component
        const allDataResponse = await getIndexDBData(STORES.PROJECTS, {
          filters: otherFilters,
          sortKey: null,
          sortOrder: null,
        });

        if (allDataResponse && typeof allDataResponse !== "string") {
          let allProjects = allDataResponse.object || [];

          // If we have projectSeqs from API, filter projects to only include those
          if (hasAnyVerificationStatus && projectSeqs && Array.isArray(projectSeqs)) {
            allProjects = allProjects.filter(project => projectSeqs.includes(project.projectSeq));
          }

          // Filter by search criteria
          allProjects = filterProjectsBySearch(allProjects, productSeqFilter, selectedProductName, basicSearchTerm);

          const sortedProjects =
            dbSortKey === TABLE_COLUMNS.VERIFICATION_STATUS
              ? sortByVerificationStatus(allProjects, dbSortOrder)
              : sortByProductName(allProjects, dbSortOrder);

          // Apply pagination after sorting
          const totalCount = sortedProjects.length;
          const start = Math.max(startRow, 0);
          const end = start + pageSize;
          const paginatedProjects = sortedProjects.slice(start, end);

          setProjectList(paginatedProjects);
          setTotalData(totalCount);
          setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
        }
      } else {
        // Normal sorting for other columns
        if (hasAnyVerificationStatus && projectSeqs && Array.isArray(projectSeqs)) {
          // If we have projectSeqs, get all data first then filter manually
          const allDataResponse = await getIndexDBData(STORES.PROJECTS, {
            filters: otherFilters,
            sortKey: dbSortKey,
            sortOrder: dbSortOrder,
            // Don't pass pageSize to get all filtered results
          });

          if (allDataResponse && typeof allDataResponse !== "string") {
            let allProjects = allDataResponse.object || [];

            // Filter projects by projectSeqs
            let filteredProjects = allProjects.filter(project => projectSeqs.includes(project.projectSeq));

            // Filter by search criteria
            filteredProjects = filterProjectsBySearch(filteredProjects, productSeqFilter, selectedProductName, basicSearchTerm);

            // Apply pagination after filtering
            const totalCount = filteredProjects.length;
            const start = Math.max(startRow, 0);
            const end = start + pageSize;
            const paginatedProjects = filteredProjects.slice(start, end);

            setProjectList(paginatedProjects);
            setTotalData(totalCount);
            setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
          }
        } else {
          // No projectSeqs filtering needed, use normal pagination
          if (productSeqFilter || basicSearchTerm) {
            // If we have product name filter or basic search, we need to get all data first then filter client-side
            const allDataResponse = await getIndexDBData(STORES.PROJECTS, {
              filters: otherFilters,
              sortKey: dbSortKey,
              sortOrder: dbSortOrder,
              // Don't pass pageSize to get all filtered results
            });

            if (allDataResponse && typeof allDataResponse !== "string") {
              let allProjects = allDataResponse.object || [];

              // Filter by search criteria
              const filteredProjects = filterProjectsBySearch(allProjects, productSeqFilter, selectedProductName, basicSearchTerm);

              // Apply pagination after filtering
              const totalCount = filteredProjects.length;
              const start = Math.max(startRow, 0);
              const end = start + pageSize;
              const paginatedProjects = filteredProjects.slice(start, end);

              setProjectList(paginatedProjects);
              setTotalData(totalCount);
              setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
            }
          } else {
            // No product name filtering, use normal pagination
            const response = await getIndexDBData(STORES.PROJECTS, {
              pageSize,
              startRow,
              filters: otherFilters,
              sortKey: dbSortKey,
              sortOrder: dbSortOrder,
            });

            if (response && typeof response !== "string") {
              setProjectList(response.object || []);
              const totalCount = response.objectCount || 0;
              setTotalData(totalCount);
              setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching projects from IndexedDB:", error);
    } finally {
      setIsProjectListLoading(false);
    }
  };

  const searchHandler = useCallback((inputData) => {
    setSearchCriteria((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="project-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="project-list-header">
        <div className="flex items-center justify-between">
          <h1
            className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}
            data-testid="project-list-title"
          >
            {t("project.projectList")}
          </h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`} data-testid="project-list-controls">
        <TableHeaderControls
          controlsTestId="project-list-table-header-controls"
          searchInputTestId="project-list-search-input"
          addButtonTestId="project-list-add-button"
          deleteButtonTestId="project-list-delete-button"
          advancedSearchButtonTestId="project-list-advanced-search-button"
          searchField="search"
          searchValue={searchCriteria.search ?? ""}
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onDeleteProject={handleDeleteProject}
          onAdd={handleAddProject}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("project.addProject")}
          titleDeleteButton={t("project.deleteProject")}
          isSearching={isSearching}
          isDisplayAddButton={hasAddPermission}
          isDisplayDeleteButton={hasDeletePermission}
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="project-list-advanced-search">
          <AdvancedSearch
            data-testid="project-list-advanced-search-panel"
            title={t("project.advancedSearch")}
            searchFields={allAdvancedSearchFields}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Project Table */}
      <div className="flex-grow min-w-0 overflow-hidden" data-testid="project-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={projectList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isProjectListLoading || isSearching}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="projectSeq"
          shouldShowGoToPage={true}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          showItemsPerPageSelector={true}
          isCheckboxDisabled={(row) => !canDeleteProject(row)}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          tableStyle="table-fixed min-w-[1100px]"
          sortable={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="project-list-delete-modal"
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("project.deleteProjectConfirmationTitle")}
        description={`${t("project.deleteProjectConfirmationDescription")} ${selectedRows.length} ${t("project.deleteProjectConfirmationDescriptionCount")}. ${t("project.deleteProjectConfirmationDescriptionCannotBeUndone")}`}
        loading={deleteLoading}
      />
    </div>
  );
};

export default ProjectList;
